import { useState } from 'react';
import { toast } from 'react-hot-toast';
import apiClient from '../api/client';
import { THEME } from '../theme/themeColors';

const BRAND       = THEME.primary;
const BRAND_LIGHT = THEME.primaryLight;
const BRAND_DARK  = THEME.primaryHover;
const ON_BRAND    = THEME.onPrimary;

const labelStyle = {
  fontSize: '10px', fontWeight: 600, color: '#495057',
  textTransform: 'uppercase', letterSpacing: '0.3px',
  marginBottom: '3px', display: 'block'
};

const inputStyle = {
  height: '28px', fontSize: '12px', padding: '2px 8px',
  border: '1px solid #dee2e6', borderRadius: '4px', width: '100%', outline: 'none',
};

export default function SpecificationDocumentModal({ ticket, domain, onClose }) {
  const [name,      setName]      = useState('');
  const [comment,   setComment]   = useState('');
  const [file,      setFile]      = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async () => {
    if (!file) { toast.error('Please select a file'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', name);
      formData.append('comment', comment);
      formData.append('domain', domain);
      formData.append('ticketId', ticket.bgTicketId);
      formData.append('ticketNumber', ticket.szTicketNumber);
      await apiClient.post(`/api/tickets/${ticket.bgTicketId}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document uploaded successfully');
      onClose();
    } catch {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <style>{`
        .sdm-overlay { position:fixed;inset:0;background:rgba(0,0,0,0.55);z-index:1050;display:flex;align-items:center;justify-content:center; }
        .sdm-modal { background:#fff;border-radius:8px;width:min(440px,96vw);display:flex;flex-direction:column;overflow:hidden;box-shadow:0 8px 32px rgba(0,0,0,0.18); }
        .sdm-header { background:${BRAND};padding:8px 14px;display:flex;align-items:center;justify-content:space-between;flex-shrink:0; }
        .sdm-header h6 { margin:0;color:${ON_BRAND};font-size:13px;font-weight:700;letter-spacing:0.2px; }
        .sdm-header .badge { font-size:11px;color:${ON_BRAND};background:rgba(255,255,255,0.22);padding:2px 10px;border-radius:20px;margin-left:8px; }
        .sdm-close { background:none;border:none;color:${ON_BRAND};font-size:18px;line-height:1;cursor:pointer;padding:0 2px;opacity:0.85; }
        .sdm-close:hover { opacity:1; }
        .sdm-body { padding:12px 14px;display:flex;flex-direction:column;gap:8px; }
        .sdm-section { border:1px solid #e9ecef;border-radius:6px;overflow:hidden; }
        .sdm-section-title { font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.4px;color:${BRAND_DARK};background:${BRAND_LIGHT};padding:5px 10px;border-bottom:1px solid ${THEME.alpha(0.25)}; }
        .sdm-section-body { padding:10px;display:flex;flex-direction:column;gap:8px; }
        .sdm-input-focus:focus { border-color:${BRAND} !important;box-shadow:0 0 0 2px ${BRAND_LIGHT} !important; }
        .sdm-textarea { width:100%;font-size:12px;padding:6px 8px;border:1px solid #dee2e6;border-radius:4px;resize:vertical;min-height:72px;line-height:1.4;outline:none;font-family:inherit; }
        .sdm-textarea:focus { border-color:${BRAND};box-shadow:0 0 0 2px ${BRAND_LIGHT}; }
        .sdm-btn { height:28px;font-size:11px;font-weight:600;padding:0 16px;border-radius:4px;border:none;cursor:pointer;display:inline-flex;align-items:center;gap:5px; }
        .sdm-btn-primary { background:${BRAND};color:${ON_BRAND}; }
        .sdm-btn-primary:hover:not(:disabled) { background:${BRAND_DARK}; }
        .sdm-btn-primary:disabled { background:#6c757d;color:#fff;cursor:not-allowed; }
        .sdm-btn-ghost { background:none;border:none;color:#6c757d;font-size:11px;padding:0 8px;cursor:pointer;height:28px;border-radius:4px; }
        .sdm-btn-ghost:hover { background:#f8f9fa; }
        .sdm-footer { padding:7px 14px;border-top:1px solid #e9ecef;display:flex;justify-content:flex-end;gap:6px;background:#fafafa; }
        .sdm-spinner { width:10px;height:10px;border:2px solid rgba(255,255,255,0.4);border-top-color:#fff;border-radius:50%;animation:sdm-spin 0.7s linear infinite;display:inline-block; }
        @keyframes sdm-spin { to { transform:rotate(360deg); } }
      `}</style>

      <div className="sdm-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="sdm-modal">

          {/* Header */}
          <div className="sdm-header">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <h6>Upload Document</h6>
              {ticket?.szTicketNumber && <span className="badge">{ticket.szTicketNumber}</span>}
            </div>
            <button className="sdm-close" onClick={onClose}>×</button>
          </div>

          <div className="sdm-body">
            <div className="sdm-section">
              <div className="sdm-section-title">Document Details</div>
              <div className="sdm-section-body">

                <div>
                  <label style={labelStyle}>Name</label>
                  <input style={inputStyle} className="sdm-input-focus" type="text"
                    value={name} onChange={e => setName(e.target.value)}
                    placeholder="Enter document name..." autoComplete="off" />
                </div>

                <div>
                  <label style={labelStyle}>Comments</label>
                  <textarea className="sdm-textarea" value={comment}
                    onChange={e => setComment(e.target.value)}
                    placeholder="Add a comment (optional)..." />
                </div>

                <div>
                  <label style={labelStyle}>File</label>
                  <input style={{ ...inputStyle, height: 'auto', padding: '3px 6px' }}
                    className="sdm-input-focus" type="file"
                    onChange={e => setFile(e.target.files[0] || null)} />
                </div>

              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="sdm-footer">
            <button className="sdm-btn sdm-btn-ghost" onClick={onClose}>Close</button>
            <button className="sdm-btn sdm-btn-primary" onClick={handleUpload} disabled={!file || uploading}>
              {uploading ? <><span className="sdm-spinner" />Uploading...</> : 'Upload'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}